import React, {useCallback, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {
  IconButton,
  Stack,
  Chat1Icon,
  SupportCenterIcon,
  Button,
  Divider,
  PencilEditNoteIcon,
  InfoCircleIcon,
  Tooltip
} from '@customink/pigment-react';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';

import {useNavigate} from 'react-router-dom';
import {metrics} from '~/vendor/metrics';
import {getCurrentDesignName, isFetching} from '~/selectors/DesignSelectors';
import {selectIsCurrentDesignInCart} from '~/slices/cartSlice';
import {isEditDesignInCartMode} from '~/utils/url';

import './CiHeaderDesignName.scss';

export const CiHeaderDesignName = () => {
  const navigate = useNavigate();
  const isFetchingDesign = useSelector(isFetching);
  const originalDesignName = useSelector(getCurrentDesignName);
  const isCurrentDesignInCart = useSelector(selectIsCurrentDesignInCart);
  const isDesignInCartMode = isEditDesignInCartMode();
  const handleNavigateToHelp = useCallback(() => {
    navigate('/help');
  }, []);
  const handleNavigateToSave = useCallback(() => {
    metrics.fireEvent('lab ways to order', 'save');
    navigate('/next/saveForm');
  }, []);

  useEffect(() => {
    window.setChatElements?.();
  }, []);
  const designName = useMemo(() => {
    if (isFetchingDesign) {
      return 'Loading...';
    }
    if (originalDesignName) {
      return originalDesignName;
    }
    return 'Untitled Design';
  }, [originalDesignName, isFetchingDesign]);
  const isCurrentDesignInCartAndInEditMode =
    isCurrentDesignInCart && isDesignInCartMode;
  const isCurrentDesignInCartAndNotInEditMode =
    isCurrentDesignInCart && !isDesignInCartMode;

  return (
    <Stack
      justifyContent="space-between"
      direction="row"
      alignItems="center"
      spacing={2}>
      <Stack direction="row" alignItems="center">
        <Divider orientation="vertical" flexItem sx={{mx: '20px'}} />
        <Stack direction="row" alignItems="center">
          <Button
            variant="link"
            sx={{
              cursor: 'pointer',
              color: '#00000091',
              ':hover': {
                backgroundColor: designTokens.color.gray.hover.original.value
              },
              '&.Mui-disabled': {
                color: designTokens.color.gray.slateDark.value
              }
            }}
            disabled={!isFetchingDesign && originalDesignName}
            size="medium"
            onClick={handleNavigateToSave}
            endIcon={
              !originalDesignName && !isFetchingDesign && <PencilEditNoteIcon />
            }>
            {designName}
          </Button>
          {!isCurrentDesignInCartAndInEditMode && (
            <Button
              sx={{marginLeft: '12px'}}
              size="small"
              variant="secondary"
              onClick={handleNavigateToSave}>
              Save | Share
            </Button>
          )}
          {isCurrentDesignInCartAndNotInEditMode && (
            <Tooltip
              arrow
              sx={{marginLeft: '8px'}}
              placement="right"
              title="This design is in your cart. Saving will create a new design.">
              <IconButton color="secondary">
                <InfoCircleIcon
                  fontSize="inherit"
                  sx={{width: '24px', height: '24px'}}
                />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" alignItems="center" justifyItems="center">
        <IconButton
          onClick={handleNavigateToHelp}
          className="ndx-save-menu-help-icon"
          sx={{
            width: '40px',
            height: '40px',
            display: {md: 'flex', lg: 'none'}
          }}>
          <SupportCenterIcon
            htmlColor={designTokens.color.gray.slateDefault.value}
            fontSize="inherit"
            sx={{width: '24px', height: '24px'}}
          />
        </IconButton>
        <IconButton
          id="LP_"
          className="ndx-save-menu-help-icon"
          sx={{
            width: '40px',
            height: '40px',
            display: {md: 'flex', lg: 'none'}
          }}>
          <Chat1Icon
            htmlColor={designTokens.color.gray.slateDefault.value}
            fontSize="inherit"
            sx={{width: '24px', height: '24px'}}
          />
        </IconButton>
        <Stack
          sx={{display: {md: 'none', lg: 'flex'}}}
          direction="row"
          spacing={0.5}
          gap={2}
          alignItems="center">
          <customer-contact />
          <customer-chat />
        </Stack>
        <Divider orientation="vertical" flexItem sx={{mx: '16px'}} />
      </Stack>
    </Stack>
  );
};
